import {
  BlockchainUrls,
  ChainsConfig,
  EBlockchainFeature,
  EBlockchainType,
} from '../common';

import { ChainID, GroupedBlockchainType, Chain, ChainURL } from '../types';
import { mapChainName } from './mapChainName';
import { isEvmExtension } from './isEvmExtension';
import { isTestnetPremiumOnly } from './isTestnetPremiumOnly';
import { isDevnetPremiumOnly } from './isDevnetPremiumOnly';
import { isSignetPremiumOnly } from './isSignetPremiumOnly';

type ChainsResult = Record<string, Chain[]>;

const getExtensions = (chains: Chain[]) => {
  return chains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Extension && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

const getBeacons = (chains: Chain[]) => {
  return chains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Beacon && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

const getOpnodes = (chains: Chain[]) => {
  return chains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Opnode && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

const getVrpcs = (chains: Chain[]) => {
  return chains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Vrpc && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

interface GetExtendedChainsArguments {
  chains: Chain[];
  extensions: GroupedBlockchainType;
  beacons: GroupedBlockchainType;
  opnodes: GroupedBlockchainType;
  vrpcs: GroupedBlockchainType;
}

const getExtendedChains = ({
  beacons,
  chains,
  extensions,
  opnodes,
  vrpcs,
}: GetExtendedChainsArguments) => {
  return chains.reduce<Chain[]>((result, chain) => {
    const { id, type } = chain;

    if (
      type !== EBlockchainType.Extension &&
      type !== EBlockchainType.Beacon &&
      type !== EBlockchainType.Opnode &&
      type !== EBlockchainType.Vrpc
    ) {
      const evmExtension = (extensions[id] || []).find(extension =>
        isEvmExtension(extension.id),
      );

      result.push({
        ...chain,
        extensions: evmExtension
          ? [
              evmExtension,
              ...extensions[id].filter(
                extension => !isEvmExtension(extension.id),
              ),
            ]
          : extensions[id],
        beacons: beacons[id],
        opnodes: opnodes[id],
        vrpcs: vrpcs[id],
      });
    }

    return result;
  }, []);
};

const getTestnets = (extendedChains: Chain[]) => {
  return extendedChains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Testnet && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

const getSignets = (extendedChains: Chain[]) => {
  return extendedChains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Signet && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

const getDevnets = (extendedChains: Chain[]) => {
  return extendedChains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends, type } = chain;

    if (type === EBlockchainType.Devnet && chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});
};

interface AddExtensionsArguments {
  extendedChains: Chain[];
  testnets: GroupedBlockchainType;
  devnets: GroupedBlockchainType;
  signets: GroupedBlockchainType;
  opnodes: GroupedBlockchainType;
}

const addExtensions = ({
  devnets,
  extendedChains,
  opnodes,
  testnets,
  signets,
}: AddExtensionsArguments) => {
  return extendedChains.reduce<Chain[]>((result, chain) => {
    const { id, type } = chain;

    if (
      type !== EBlockchainType.Testnet &&
      type !== EBlockchainType.Devnet &&
      type !== EBlockchainType.Signet
    ) {
      result.push({
        ...chain,
        testnets: testnets[id],
        devnets: devnets[id],
        opnodes: opnodes[id],
        signets: signets[id],
      });
    }

    return result;
  }, []);
};

const addExtenders = (chains: Chain[]) => {
  const extenders = chains.reduce<ChainsResult>((result, chain) => {
    const { chainExtends } = chain;

    if (chainExtends) {
      result[chainExtends] = result[chainExtends]
        ? [...result[chainExtends], chain]
        : [chain];
    }

    return result;
  }, {});

  return chains.reduce<Chain[]>((result, chain) => {
    const { chainExtends, id } = chain;

    if (!chainExtends) {
      result.push({
        ...chain,
        extenders: extenders[id],
      });
    }

    return result;
  }, []);
};

const addPremiumOnly = (chains: Chain[]) => {
  chains.forEach(item => {
    const hasTestnets = item.testnets && item.testnets.length > 0;
    const protectedTestnets = hasTestnets ? isTestnetPremiumOnly(item) : true;
    const hasDevnets = item.devnets && item.devnets.length > 0;
    const protectedDevnets = hasDevnets ? isDevnetPremiumOnly(item) : true;
    const hasSignets = item.signets && item.signets.length > 0;
    const protectedSignets = hasSignets ? isSignetPremiumOnly(item) : true;

    item.premiumOnly =
      item.isMainnetPremiumOnly &&
      protectedTestnets &&
      protectedDevnets &&
      protectedSignets;
  });

  return chains;
};

const getURLs = ({
  enterpriseURLs,
  enterpriseWsURLs,
  grpcURLs,
  restURLs,
  rpcURLs,
  wsURLs,
}: BlockchainUrls) => {
  const template = new Array(
    Math.max(restURLs.length, rpcURLs.length, grpcURLs.length),
  ).fill('');

  return template.map<ChainURL>((_, index) => ({
    enterprise: enterpriseURLs[index],
    enterpriseWs: enterpriseWsURLs[index],
    grpc: grpcURLs[index],
    rest: restURLs[index],
    rpc: rpcURLs[index],
    ws: wsURLs[index],
  }));
};

const getApiChains = (data: ChainsConfig, availableChainIds?: string[]) => {
  return Object.values(data).map<Chain>(chain => {
    const { blockchain } = chain;
    const {
      coinName,
      extends: chainExtends,
      features,
      id,
      name,
      paths,
      premiumOnly,
      type,
      isPublic,
    } = blockchain;

    const isComingSoon = features.includes(EBlockchainFeature.ComingSoon);

    return {
      chainExtends,
      coinName,
      features,
      hasEnterpriseFeature: availableChainIds?.includes(id) ?? false,
      hasGRPCFeature: features.includes(EBlockchainFeature.GRPC),
      hasRESTFeature: features.includes(EBlockchainFeature.REST),
      hasRPCFeature:
        features.includes(EBlockchainFeature.RPC) ||
        features.includes(EBlockchainFeature.GRPC_WEB),
      hasWSFeature: features.includes(EBlockchainFeature.WS),
      id: id as ChainID,
      isComingSoon,
      isMainnetComingSoon: type === EBlockchainType.Mainnet && isComingSoon,
      isMainnetPremiumOnly: type === EBlockchainType.Mainnet && premiumOnly,
      isPublic,
      name: mapChainName(id as ChainID, name),
      paths,
      premiumOnly,
      type,
      urls: getURLs(chain),
    };
  });
};

export const formatChainsConfigToChains = (
  data: ChainsConfig = {},
  availableChainIds?: string[],
): Chain[] => {
  const chains = getApiChains(data, availableChainIds);

  const extensions = getExtensions(chains);
  const beacons = getBeacons(chains);
  const opnodes = getOpnodes(chains);
  const vrpcs = getVrpcs(chains);
  const extendedChains = getExtendedChains({
    chains,
    extensions,
    beacons,
    opnodes,
    vrpcs,
  });
  const testnets = getTestnets(extendedChains);
  const devnets = getDevnets(extendedChains);
  const signets = getSignets(extendedChains);
  const chainsWithExtensions = addExtensions({
    extendedChains,
    testnets,
    devnets,
    signets,
    opnodes,
  });

  const chainsWithExtenders = addExtenders(chainsWithExtensions);
  const chainsWithPremiumOnly = addPremiumOnly(chainsWithExtenders);

  return chainsWithPremiumOnly;
};
